import { useEffect, useState } from "react";
import { FormikProvider, useFormik } from "formik";
import { Button } from "../Button";
import classNames from "classnames";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import Modal from "../Modal";
import FormikInput from "../FormikInput";
import { StorageService } from "@services/StorageService";
import { ToasterService } from "@services/ToastService";
import { useService } from "@/base/hooks/useService";
import {
  addEmail,
  addEmailReset,
  toggleAddEmailModal,
} from "@redux/addEmail/actions";
import { localstorageKeys } from "@const/store";
import { MAX_EMAIL_LENGTH } from "@validation/lengthConstants";
import { initialValues, validationSchema } from "./form";

import styles from "./index.module.scss";
import VerifyEmail from "../VerifyEmail";
import { toggleSignInModal } from "@redux/signIn/actions";

const AddEmailModal = () => {
  const { t } = useTranslation("add-email");
  const { t: t1 } = useTranslation("buttons");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const storageService = useService(StorageService);
  const toastService = useService(ToasterService);
  const [email, setEmail] = useState("");
  const [isProvideEmailStep, setProvideEmailStep] = useState(true);

  const { isLoading, isSuccess, error, isProvideEmailModalVisible } =
    useSelector(({ addEmailReducer }) => addEmailReducer);

  const handleSubmit = async (values) => {
    if (values.email === email) {
      setProvideEmailStep(false);
    } else {
      setEmail(values.email);
      dispatch(addEmail(values));
      storageService.set(localstorageKeys.sentAt, Date.now());
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  const closeModal = () => {
    dispatch(toggleAddEmailModal(false));
  };

  const onBackClick = () => {
    setProvideEmailStep(true);
  };

  useEffect(() => {
    if (!isSuccess) return;

    storageService.set(localstorageKeys.sentAt, Date.now());
    setProvideEmailStep(false);
  }, [isSuccess]);

  useEffect(() => {
    if (!error) return;

    toastService.error(error.message);
  }, [error, toastService]);

  useEffect(() => {
    if (isProvideEmailModalVisible) return;

    dispatch(addEmailReset());
  }, [isProvideEmailModalVisible]);

  const successCallback = () => {
    closeModal();
    navigate("/");
    dispatch(toggleSignInModal(true));
  };

  return (
    <Modal
      centered
      unmountOnClose
      showCancel={false}
      showConfirm={false}
      onClose={closeModal}
      footerClassName="d-none"
      bodyClassName={styles.body}
      isOpen={isProvideEmailModalVisible}
    >
      <div
        style={{
          display: isProvideEmailStep ? "flex" : "none",
        }}
        className={classNames(
          "container w-100 h-100 flex-column justify-content-center",
          styles.content,
        )}
      >
        <h3 className="text-primary h3 text-medium text-center mb-4">
          {t("PROVIDE_EMAIL")}
        </h3>
        <p
          className={classNames(
            "text-primary text-center text-body-1",
            styles.subtitle,
          )}
        >
          {t("PLS_FILL")}
        </p>
        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit}>
            <FormikInput
              maxLength={MAX_EMAIL_LENGTH}
              label="EMAIL"
              placeholder="EMAIL"
              wrapperClassName="mb-4"
              name="email"
            />
            <Button
              size="lg"
              color="primary"
              className="w-100"
              type="submit"
              disabled={
                isLoading || !formik.isValid || !formik.values.email.length
              }
            >
              {t1("SIGN_UP")}
            </Button>
          </form>
        </FormikProvider>
      </div>

      <VerifyEmail
        email={email}
        onBackClick={onBackClick}
        isVisible={!isProvideEmailStep}
        successCallback={successCallback}
      />
    </Modal>
  );
};

export default AddEmailModal;
